/**
 * 📌 Script: pwaInstallStore.js
 * 📂 Caminho: src/stores/pwaInstallStore.js
 * 📝 Descrição: Captura o evento beforeinstallprompt e expõe
 *   método de instalação manual do PWA atual (determinado
 *   pelo manifesto injetado em main.js). Permite exibir
 *   botão "Instalar App" em qualquer componente Vue.
 *
 * 🔄 Relação com o Projeto:
 *  - Usado por VitrineFooter.vue, AnmeldungView.vue e
 *    ZeiterfassungView.vue para mostrar o botão de instalação.
 *  - Registrado via store.init() em main.js após mount.
 *
 * 🧩 Exports:
 *  - `usePwaInstallStore()` — store Pinia
 *
 * 🔗 Dependências:
 *  - **Utilitários internos:** Pinia
 */

import { defineStore } from "pinia";

export const usePwaInstallStore = defineStore("pwaInstall", {
  state: () => ({
    // Evento capturado do browser; null até o browser emiti-lo
    promptEvent: null,
    // true enquanto o browser ainda pode mostrar o prompt
    canInstall: false,
    // true após instalação confirmada
    installed: false,
  }),

  actions: {
    /**
     * Registra o listener de beforeinstallprompt.
     * Deve ser chamado uma única vez, em main.js.
     */
    init() {
      window.addEventListener("beforeinstallprompt", (e) => {
        // Impede o mini-infobar automático do Chrome
        e.preventDefault();
        this.promptEvent = e;
        this.canInstall = true;
      });

      window.addEventListener("appinstalled", () => {
        this.promptEvent = null;
        this.canInstall = false;
        this.installed = true;
      });
    },

    /**
     * Dispara o prompt de instalação nativo do browser.
     * @returns {Promise<'accepted'|'dismissed'|null>}
     */
    async install() {
      if (!this.promptEvent) return null;
      this.promptEvent.prompt();
      const { outcome } = await this.promptEvent.userChoice;
      if (outcome === "accepted") {
        this.installed = true;
        this.canInstall = false;
        this.promptEvent = null;
      }
      return outcome;
    },
  },
});
